"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, Linkedin, Menu, X } from "lucide-react";

import { LandingRequestDemoModal } from "@/components/landing/layouts/lumina/LandingRequestDemoModal";
import {
  openRequestDemo,
  RequestAccessLink,
} from "@/components/marketing/shared/MarketingCTA";
import { NoScriptReveal } from "@/components/marketing/shared/NoScriptReveal";
import { useMarketingNavScroll } from "@/components/marketing/shared/useMarketingNavScroll";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { aboutPageStyles } from "./aboutPageStyles";
import { SUPPORT_EMAIL } from "@/lib/contact";

const teamMembers = [
  {
    name: "Dom Carfagno",
    title: "Founder and CEO",
    href: "https://linkedin.com/in/dom-carfagno-454987326",
    image: "/images/zom-marketing/team/team-dom-carfagno.jpg",
  },
  {
    name: "Joshua Cureton",
    title: "Strategy & Operations",
    href: "https://linkedin.com/in/joshua-cureton-079b372ba",
    image: "/images/zom-marketing/team/team-joshua-cureton.jpg",
  },
  {
    name: "Apinan Yogaratnam",
    title: "Senior AI Engineer",
    href: "https://linkedin.com/in/apinanyogaratnam",
    image: "/images/zom-marketing/team/team-apinan-yogaratnam.jpg",
  },
  {
    name: "Kamal Feracho",
    title: "UI/UX",
    href: "https://linkedin.com/in/kamal-feracho-075a5a1aa",
    image: "/images/zom-marketing/team/team-kamal-feracho.jpg",
  },
  {
    name: "Dinesh Yadav",
    title: "Backend Engineer",
    href: "https://linkedin.com/in/dineshyd",
    image: "/images/zom-marketing/team/team-dinesh-yadav.jpg",
  },
  {
    name: "Ben Vecchiarelli",
    title: "Business Development",
    href: "https://linkedin.com/in/ben-vecchiarelli-7ab937364",
    image: "/images/zom-marketing/team/team-ben-vecchiarelli.jpg",
  },
] as const;

const developmentPartnerPromises = [
  {
    number: "01",
    title: "You choose the workflows.",
    body: "A development partner decides which parts of the practice Zom takes on first. We build against the work your firm actually runs, in the order you want it, rather than a fixed onboarding script.",
  },
  {
    number: "02",
    title: "Unlimited access to the development team.",
    body: "Not a support queue and not a quarterly check-in. Development partners talk to the people writing the code, raise what is not working, and see it addressed while the engagement is live.",
  },
  {
    number: "03",
    title: "You set the meeting cadence.",
    body: "Weekly, monthly, or only when something needs a decision. The rhythm of the engagement is yours to set, and it can change as the work changes.",
  },
] as const;

function usePageReveals() {
  useEffect(() => {
    const revealItems = Array.from(
      document.querySelectorAll<HTMLElement>(".zmk-about-root [data-sreveal]"),
    );
    const lineItems = Array.from(
      document.querySelectorAll<HTMLElement>(".zmk-about-root [data-sline]"),
    );

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      [...revealItems, ...lineItems].forEach((item) =>
        item.classList.add("is-visible"),
      );
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );

    [...revealItems, ...lineItems].forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);
}

function AboutNav() {
  const { scrolled, hidden, setHidden } = useMarketingNavScroll();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleRequestAccess = () => {
    setMobileOpen(false);
    openRequestDemo("about_nav");
  };

  return (
    <>
      <header
        className={cn(
          "zmk-about-nav",
          scrolled && "is-scrolled",
          hidden && !mobileOpen && "is-hidden",
        )}
        data-screen-label="Nav"
        onFocus={() => setHidden(false)}
      >
        <nav className="zmk-about-nav__inner" aria-label="Main navigation">
          <Link className="zmk-about-nav__brand" href="/" aria-label="Zom home">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
              priority
            />
          </Link>
          <div className="zmk-about-nav__links">
            <Link href="/about">About</Link>
            <Link href="/security">Security</Link>
            <Button
              type="button"
              variant="ghost"
              className="zmk-about-nav__request"
              onClick={handleRequestAccess}
            >
              Request access
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="zmk-about-nav__menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((current) => !current)}
          >
            {mobileOpen ? <X aria-hidden /> : <Menu aria-hidden />}
          </Button>
        </nav>
      </header>

      <div className={cn("zmk-about-mobile-menu", mobileOpen && "is-open")}>
        <Link href="/about" onClick={() => setMobileOpen(false)}>
          About
        </Link>
        <Link href="/security" onClick={() => setMobileOpen(false)}>
          Security
        </Link>
        <Button
          type="button"
          className="zmk-about-button"
          onClick={handleRequestAccess}
        >
          Request access
        </Button>
      </div>
    </>
  );
}

function HeroSection() {
  return (
    <section className="zmk-about-section zmk-about-hero">
      <div className="zmk-about-bloom" aria-hidden />
      <div className="zmk-about-grain" aria-hidden />
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          Mission
        </p>
        <h1 className="zmk-about-display" data-sreveal>
          Better intelligence behind every financial decision.
        </h1>
        <p data-sreveal>
          Zom&apos;s mission is to make high-quality financial intelligence
          universal. We are starting with independent RIAs, where client,
          portfolio, workflow, compliance, and firm context can inform every
          decision. Zom prepares the work. The advisor remains the fiduciary and
          decides what is recommended, approved, sent, or implemented.
        </p>
      </div>
    </section>
  );
}

function TeamSection() {
  return (
    <section className="zmk-about-section zmk-about-team">
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          The people behind Zom
        </p>
        <div className="zmk-about-sline" data-sline aria-hidden />
        <div className="zmk-about-team__list" data-sreveal>
          {teamMembers.map((member) => (
            <Link
              className="zmk-about-team__row"
              href={member.href}
              key={member.name}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="zmk-about-team__image">
                <Image src={member.image} alt={member.name} fill sizes="72px" />
              </span>
              <span>
                <span className="zmk-about-team__name">{member.name}</span>
                <span className="zmk-about-mono zmk-about-team__title">
                  {member.title}
                </span>
              </span>
              <Linkedin className="zmk-about-team__icon" aria-hidden />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisionSection() {
  return (
    <section className="zmk-about-section zmk-about-statement">
      <div className="zmk-about-bloom" aria-hidden />
      <div className="zmk-about-grain" aria-hidden />
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          Vision
        </p>
        <h2 className="zmk-about-display" data-sreveal>
          The work that matters
          <br />
          happens between people.
        </h2>
        <p data-sreveal>
          Advisory firms already have the data and judgment they need. The
          problem is that the context behind each decision lives across
          disconnected systems and people. Zom brings the relevant context into
          the advisor&apos;s work so the firm can prepare, review, and make
          decisions with a clearer picture.
        </p>
      </div>
    </section>
  );
}

function FounderSection() {
  return (
    <section className="zmk-about-section zmk-about-founder">
      <div className="zmk-about-wrap">
        <div data-sreveal>
          <p className="zmk-about-mono">Why we&apos;re building this</p>
          <div className="zmk-about-founder__identity">
            <p className="zmk-about-founder__name">Dom Carfagno</p>
            <p className="zmk-about-founder__role">Founder and CEO</p>
          </div>
        </div>
        <div className="zmk-about-founder__quote" data-sreveal>
          <p>
            &quot;Advisors are expected to understand every client&apos;s
            portfolio, goals, family situation, and prior decisions, but that
            context is spread across systems and people. We are building Zom so
            the firm does not have to reconstruct that picture every time work
            begins.
          </p>
          <p>
            Zom brings the relevant information and approved context into the
            request, prepares the work, and leaves the decision with the
            advisor.&quot;
          </p>
        </div>
      </div>
    </section>
  );
}

function InstitutionalMemorySection() {
  return (
    <section
      className="zmk-about-section zmk-about-memory"
      aria-labelledby="institutional-memory"
    >
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          Institutional memory
        </p>
        <h2
          className="zmk-about-display"
          id="institutional-memory"
          data-sreveal
        >
          Your firm&apos;s judgment carries forward.
        </h2>
        <p data-sreveal>
          Zom is building institutional memory that preserves approved context
          behind client relationships, promises, decisions, exceptions, and firm
          practices. During an active advisor request, Zom can use the relevant
          memory alongside current data from connected systems. Advisors control
          what is remembered, where it applies, and when it is corrected or
          removed.
        </p>
      </div>
    </section>
  );
}

function NewsSection() {
  const [open, setOpen] = useState(false);

  return (
    <section className="zmk-about-section zmk-about-news">
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          News
        </p>
        <div className="zmk-about-sline" data-sline aria-hidden />
        <div className="zmk-about-news__item" data-sreveal>
          <Button
            type="button"
            variant="ghost"
            className="zmk-about-news__toggle min-w-0 whitespace-normal"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <span>
              <span className="zmk-about-news__tags">
                <span>The College Today</span>
                <span aria-hidden>+</span>
                <span>Press</span>
              </span>
              <span className="zmk-about-news__headline">
                Zom&apos;s founder &amp; CEO profiled for building an operating
                intelligence layer for financial advisors
              </span>
              <span className="zmk-about-news__byline">
                The College of Charleston - July 23, 2026
              </span>
            </span>
            <span className="zmk-about-news__icon" aria-hidden />
          </Button>
          <div className={cn("zmk-about-news__body", open && "is-open")}>
            <div className="zmk-about-news__body-inner">
              <div className="zmk-about-news__body-content">
                <p>
                  The College Today spoke with Zom&apos;s founder about the gap
                  between how much an advisory team is expected to know about
                  each client and how many hours actually exist in a week. The
                  piece traces the path from that observation to building an
                  operating intelligence layer purpose-built for independent
                  RIAs.
                </p>
                <p>
                  It&apos;s an early look at the thinking behind the product and
                  where a smaller firm can find leverage without adding
                  headcount.
                </p>
                <Link
                  href="https://today.charleston.edu/2026/07/23/dom-carfagno-startup-zom-ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="zmk-about-news__link"
                >
                  Read the original story
                  <ArrowRight aria-hidden />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DevelopmentPartnerSection() {
  return (
    <section
      className="zmk-about-section zmk-about-partner"
      aria-labelledby="development-partners"
    >
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          Development partners
        </p>
        <h2
          className="zmk-about-display"
          id="development-partners"
          data-sreveal
        >
          A small number of firms build this with us.
        </h2>
        <p className="zmk-about-partner__lede" data-sreveal>
          Zom is being built alongside a limited group of independent RIAs. A
          development partner is not an early customer on a discount. It is a
          firm that shapes what gets built, and the commitments below apply for
          the length of the engagement.
        </p>
        <div className="zmk-about-sline" data-sline aria-hidden />
        <ol className="zmk-about-partner__list" data-sreveal>
          {developmentPartnerPromises.map((promise) => (
            <li className="zmk-about-partner__row" key={promise.number}>
              <span className="zmk-about-mono zmk-about-partner__number">
                {promise.number}
              </span>
              <span className="zmk-about-partner__body">
                <span className="zmk-about-partner__title">
                  {promise.title}
                </span>
                <span className="zmk-about-partner__copy">{promise.body}</span>
              </span>
            </li>
          ))}
        </ol>
        <p className="zmk-about-partner__note" data-sreveal>
          These commitments are specific to development partners and do not
          describe standard access.
        </p>
      </div>
    </section>
  );
}

function CompanyCtaSection() {
  return (
    <section className="zmk-about-section zmk-about-cta">
      <div className="zmk-about-bloom zmk-about-bloom--cta" aria-hidden />
      <div className="zmk-about-grain" aria-hidden />
      <div className="zmk-about-wrap">
        <p className="zmk-about-mono" data-sreveal>
          Request access
        </p>
        <h2 className="zmk-about-display" data-sreveal>
          Creating a world where financial advisory is driven by human
          connection, not busywork.
        </h2>
        <div className="zmk-about-cta__actions" data-sreveal>
          <Button asChild className="zmk-about-button">
            <RequestAccessLink source="about_development_partner">
              Request access
              <ArrowRight aria-hidden />
            </RequestAccessLink>
          </Button>
          <span className="zmk-about-cta__email">{SUPPORT_EMAIL}</span>
        </div>
        <p className="zmk-about-cta__secondary" data-sreveal>
          Interested in working at Zom?{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>Get in touch</Link>.
        </p>
      </div>
    </section>
  );
}

function AboutFooter() {
  return (
    <footer className="zmk-about-footer">
      <div className="zmk-about-footer__inner">
        <div className="zmk-about-footer__grid">
          <div className="zmk-about-footer__brand">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
            />
            <p>Operating intelligence for independent RIAs.</p>
          </div>
          <div className="zmk-about-footer__col">
            <h3>About</h3>
            <Link href="/security">Security</Link>
            <Link href="/about">About</Link>
          </div>
          <div className="zmk-about-footer__col">
            <h3>Legal</h3>
            <Link href="/privacy">Privacy</Link>
            <Link href="/terms">Terms</Link>
            <Button asChild variant="ghost">
              <RequestAccessLink source="about_footer">
                Request access
              </RequestAccessLink>
            </Button>
          </div>
        </div>
        <p className="zmk-about-footer__bottom">
          © 2026 Zom. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default function AboutPageClient() {
  usePageReveals();

  return (
    <div className="zmk-about-root">
      <style dangerouslySetInnerHTML={{ __html: aboutPageStyles }} />
      <NoScriptReveal />
      <LandingRequestDemoModal />
      <AboutNav />
      <main>
        <HeroSection />
        <TeamSection />
        <VisionSection />
        <FounderSection />
        <InstitutionalMemorySection />
        <NewsSection />
        <DevelopmentPartnerSection />
        <CompanyCtaSection />
      </main>
      <AboutFooter />
    </div>
  );
}
